<script setup lang="ts">
// Ported from flowconnect-dashboard/app/components/ui with explicit imports (no Nuxt auto-imports).
import { computed } from 'vue'
import { cn } from '../lib/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from '@lucide/vue'

const button = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium select-none transition-[background-color,border-color,color,box-shadow,transform] duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-[0_1px_0_0_rgb(255_255_255/0.15)_inset,0_6px_16px_-6px_rgb(124_58_237/0.7)] hover:bg-primary-hover',
        secondary: 'bg-muted text-foreground hover:bg-border',
        outline: 'border border-border-strong bg-card text-foreground hover:bg-muted',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        danger: 'bg-danger text-white hover:opacity-90',
        'danger-ghost': 'text-danger hover:bg-danger-soft',
        link: 'text-accent underline-offset-4 hover:underline px-0',
      },
      size: {
        xs: 'h-7 rounded-md px-2.5 text-xs',
        sm: 'h-8 rounded-md px-3 text-[13px]',
        md: 'h-9 rounded-lg px-3.5 text-sm',
        lg: 'h-11 rounded-xl px-5 text-[15px]',
        icon: 'size-9 rounded-lg',
        'icon-sm': 'size-8 rounded-md',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

type ButtonProps = VariantProps<typeof button>

const props = withDefaults(
  defineProps<{
    variant?: ButtonProps['variant']
    size?: ButtonProps['size']
    loading?: boolean
    disabled?: boolean
    to?: string
    href?: string
    type?: 'button' | 'submit'
    class?: string
  }>(),
  { type: 'button' },
)

const classes = computed(() => cn(button({ variant: props.variant, size: props.size }), props.class))
</script>

<template>
  <RouterLink v-if="to" :to="to" :class="classes"><slot /></RouterLink>
  <a v-else-if="href" :href="href" target="_blank" rel="noopener" :class="classes"><slot /></a>
  <button v-else :type="type" :class="classes" :disabled="disabled || loading">
    <Loader2 v-if="loading" class="animate-spin" />
    <slot />
  </button>
</template>
